import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Receipt, Plus, Trash2, Sparkles, X, ChevronDown, ChevronUp, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl, num, parseNum, numStr } from "@/lib/format";
import { Button, Chip, Field, Input } from "@/components/kit";
import PhotoCapture, { AuthImage, PhotoViewer, PdfTile, isPdf } from "@/components/conference/PhotoCapture";
import UnmatchedItems from "@/components/conference/UnmatchedItems";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const uid = () => crypto.randomUUID();
const emptyLine = () => ({ uid: uid(), product_id: "", qty: "", total: "" });

function InvoiceForm({ confId, onClose, invalidate }) {
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api("/products") });
  const [f, setF] = useState({ supplier: "", number: "", amount: "", category_id: "", photo_path: null });
  const [lines, setLines] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [err, setErr] = useState("");
  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const sum = lines.reduce((a, l) => a + parseNum(l.total), 0);

  const read = useMutation({
    mutationFn: () => api("/invoices/read", { method: "POST", body: { photo_path: f.photo_path } }),
    onSuccess: (r) => {
      setF((x) => ({ ...x, supplier: r.supplier || x.supplier, number: r.number || x.number, amount: r.amount ? numStr(r.amount) : x.amount }));
      const matched = r.lines.filter((l) => l.product_id);
      setLines(matched.map((l) => ({ uid: uid(), product_id: l.product_id, qty: numStr(l.qty), total: numStr(l.total) })));
      setUnmatched(r.lines.filter((l) => !l.product_id).map((l) => ({ ...l, uid: uid() })));
      const miss = r.lines.length - matched.length;
      toast.success(`Nota lida: ${matched.length} item(ns) reconhecido(s)${miss ? ` · ${miss} item(ns) sem produto cadastrado` : ""}. Confira antes de salvar.`);
    },
    onError: (e) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: () => api(`/conferences/${confId}/invoices`, {
      method: "POST",
      body: { ...f, amount: parseNum(f.amount) || null, lines: lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, qty: parseNum(l.qty), total: parseNum(l.total) })) },
    }),
    onSuccess: (r) => {
      invalidate();
      toast.success(lines.length ? "Nota lançada · custos atualizados" : "Nota fiscal lançada");
      r.alerts.forEach((a) => toast.warning(`Alerta de preço: ${a.product_name} +${num(a.pct, 1)}% acima da média`));
      onClose();
    },
    onError: (e) => setErr(e.message),
  });

  const submit = () => {
    setErr("");
    if (!f.supplier.trim()) return setErr("Informe o fornecedor");
    if (!f.category_id) return setErr("Selecione a categoria da nota");
    if (!parseNum(f.amount) && !sum) return setErr("Informe o valor da nota ou os itens");
    save.mutate();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="invoice-form">
      <div className="flex flex-col gap-2">
        <span className="label">Foto da nota (opcional · a IA pode preencher os dados)</span>
        {f.photo_path ? (
          <div className="flex items-center gap-3">
            {isPdf(f.photo_path) ? <PdfTile path={f.photo_path} testid="invoice-photo-preview" /> : <AuthImage path={f.photo_path} className="w-20 h-20 rounded-lg" testid="invoice-photo-preview" />}
            <div className="flex flex-col gap-2">
              <Button size="sm" icon={Sparkles} loading={read.isPending} onClick={() => read.mutate()} data-testid="read-invoice-ai-button">Ler nota com IA</Button>
              <Button size="sm" variant="ghost" onClick={() => setF({ ...f, photo_path: null })} data-testid="remove-invoice-photo">Remover foto</Button>
            </div>
          </div>
        ) : (
          <PhotoCapture allowPdf onUploaded={(p) => setF({ ...f, photo_path: p })} />
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Fornecedor"><Input placeholder="Ex.: Frigorífico Silva" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} data-testid="invoice-supplier-input" /></Field>
        <Field label="Nº da nota"><Input placeholder="Opcional" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="invoice-number-input" /></Field>
        <Field label="Valor total (R$)"><Input inputMode="decimal" placeholder="0,00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="invoice-amount-input" /></Field>
      </div>
      <div className="flex flex-col gap-2">
        <span className="label">Categoria</span>
        <div className="scroll-x">{categories.map((c) => <Chip key={c.id} active={f.category_id === c.id} onClick={() => setF({ ...f, category_id: c.id })} data-testid={`invoice-category-${c.id}`}>{c.name}</Chip>)}</div>
      </div>
      <div className="flex flex-col gap-2">
        <span className="label">Itens da nota (atualiza o custo dos produtos)</span>
        {lines.map((l, i) => (
          <div key={l.uid} className="grid grid-cols-[minmax(0,1fr)_72px_96px_38px] gap-2 items-center" data-testid={`invoice-line-${i}`}>
            <select className="input input-sm" value={l.product_id} onChange={(e) => setLine(i, "product_id", e.target.value)} data-testid={`invoice-line-product-${i}`}>
              <option value="">Produto…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
            </select>
            <input className="input input-sm text-right" inputMode="decimal" placeholder="Qtd" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`invoice-line-qty-${i}`} />
            <input className="input input-sm text-right" inputMode="decimal" placeholder="R$ total" value={l.total} onChange={(e) => setLine(i, "total", e.target.value)} data-testid={`invoice-line-total-${i}`} />
            <button className="icon-btn" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`invoice-line-remove-${i}`}><X size={15} /></button>
          </div>
        ))}
        <Button size="sm" variant="secondary" icon={Plus} className="self-start" onClick={() => setLines([...lines, emptyLine()])} data-testid="add-invoice-line-button">Adicionar item</Button>
        {lines.length > 0 && <span className="caption">Soma dos itens: {brl(sum)} · novo custo = valor ÷ quantidade</span>}
      </div>
      <UnmatchedItems items={unmatched} categories={categories} defaultCategoryId={f.category_id}
        onDismiss={(i) => setUnmatched(unmatched.filter((_, j) => j !== i))}
        onCreated={(i, line) => { setUnmatched(unmatched.filter((_, j) => j !== i)); setLines((ls) => [...ls, { uid: uid(), product_id: line.product_id, qty: numStr(line.qty), total: numStr(line.total) }]); }} />
      {err && <span className="caption" style={{ color: "var(--danger)" }} data-testid="invoice-form-error">{err}</span>}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose} data-testid="cancel-invoice-button">Cancelar</Button>
        <Button loading={save.isPending} onClick={submit} data-testid="save-invoice-button">Salvar nota</Button>
      </div>
    </div>
  );
}

function InvoiceRow({ inv, editable, onDelete, onView, onAttach }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3" data-testid={`invoice-row-${inv.id}`}>
      <div className="flex items-center gap-3">
        {inv.photo_path ? (
          isPdf(inv.photo_path) ? <PdfTile path={inv.photo_path} className="w-11 h-11 shrink-0" testid={`invoice-photo-${inv.id}`} /> : <AuthImage path={inv.photo_path} className="w-11 h-11 rounded-lg shrink-0" onClick={() => onView(inv.photo_path)} testid={`invoice-photo-${inv.id}`} />
        ) : (
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}><Receipt size={18} style={{ color: "var(--brand)" }} /></div>
        )}
        <button className="flex-1 min-w-0 text-left" onClick={() => setOpen(!open)} data-testid={`invoice-toggle-${inv.id}`}>
          <div className="label truncate">{inv.supplier}{inv.number ? ` · NF ${inv.number}` : ""}</div>
          <div className="caption">{inv.category_name} · {inv.lines.length} itens {inv.lines.length ? (open ? <ChevronUp size={12} className="inline" /> : <ChevronDown size={12} className="inline" />) : null}</div>
        </button>
        <span className="label tnum">{brl(inv.amount)}</span>
        {editable && !inv.photo_path && <PhotoCapture compact allowPdf onUploaded={(p) => onAttach(inv.id, p)} />}
        {editable && <button className="icon-btn" onClick={() => onDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}><Trash2 size={15} /></button>}
      </div>
      {open && inv.lines.map((l, i) => (
        <div key={l.id || `${l.product_id}-${l.qty}-${l.total}`} className="flex justify-between caption pl-14 pt-1.5"><span>{l.product_name} ({num(l.qty)} {l.unit})</span><span className="tnum">{brl(l.total)} · {brl(l.total / l.qty)}/{l.unit}</span></div>
      ))}
    </div>
  );
}

export default function InvoicesSection({ conf, editable, invalidate }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null);
  const total = conf.invoices.reduce((a, i) => a + i.amount, 0);
  const del = useMutation({
    mutationFn: (id) => api(`/invoices/${id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); toast.success("Nota removida"); },
    onError: (e) => toast.error(e.message),
  });
  const attach = async (id, path) => {
    await api(`/invoices/${id}/photo`, { method: "PUT", body: { photo_path: path } });
    qc.invalidateQueries({ queryKey: ["conference", conf.id] });
  };
  return (
    <div className="card" data-testid="invoices-section">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="h3">Notas fiscais de compra</div>
          <div className="caption">{conf.invoices.length} notas · total {brl(total)}</div>
        </div>
        {editable && <Button size="sm" variant="secondary" icon={ImagePlus} onClick={() => setAdding(true)} data-testid="add-invoice-button">Lançar</Button>}
      </div>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl">
          <DialogTitle className="h2">Lançar nota fiscal</DialogTitle>
          {adding && <InvoiceForm confId={conf.id} onClose={() => setAdding(false)} invalidate={invalidate} />}
        </DialogContent>
      </Dialog>
      <div className="divide mt-2">
        {conf.invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} editable={editable} onDelete={(id) => del.mutate(id)} onView={setViewing} onAttach={attach} />)}
      </div>
      <PhotoViewer path={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
